import { ButtonColor, ButtonSize, ButtonVariant } from "enums/Button";
import styled, { DefaultTheme } from "styled-components";
import { focusOutline } from "styles/focus";
import PreLoader from "../PreLoader/PreLoader";
import {
  getBackgroundColor,
  getFilledTextColor,
  variantStyles,
} from "./helperStyles";

// What each button colour paints. A fill has to carry text on it and a text
// colour has to read on the page, so the two differ where one token cannot do
// both: primary and the payment green (docs/BUTTONS.md has the ratios).
export const resolveButtonColor = (
  colorKey: ButtonColor,
  variant: ButtonVariant,
  theme: DefaultTheme,
): string => {
  const isFilled = variant === ButtonVariant.Filled;

  switch (colorKey) {
    case ButtonColor.Primary:
      return isFilled ? theme.buttonPrimary : theme.primaryDark;
    case ButtonColor.Secondary:
      return isFilled ? theme.buttonSecondary : theme.secondaryDark;
    case ButtonColor.Danger:
      return theme.buttonDanger;
    case ButtonColor.Neutral:
      return theme.black;
    case ButtonColor.Muted:
      return theme.buttonMuted;
  }
};

// The label on a fill. The pay mint names its own forest green; every other
// fill takes black or white by contrast.
export const resolveFilledLabel = (
  colorKey: ButtonColor,
  theme: DefaultTheme,
): string =>
  colorKey === ButtonColor.Secondary
    ? theme.buttonPayLabel
    : getFilledTextColor(
        resolveButtonColor(colorKey, ButtonVariant.Filled, theme),
        theme,
      );

// The roles a control outside the kit Button can take (IconButton,
// LinkButton). Each paints what a text Button of that role paints, so the
// table above stays the only place a role meets a token.
export type ButtonTone = "muted" | "primary" | "danger";

const TONE_COLORS: Record<ButtonTone, ButtonColor> = {
  muted: ButtonColor.Muted,
  primary: ButtonColor.Primary,
  danger: ButtonColor.Danger,
};

export const toneColor = (tone: ButtonTone, theme: DefaultTheme) =>
  resolveButtonColor(TONE_COLORS[tone], ButtonVariant.Basic, theme);

// Two fixed heights: 36px and 46px, the outline's hairline included.
const SIZES: Record<
  ButtonSize,
  { block: number; inline: number; font: string; line: string }
> = {
  [ButtonSize.Small]: { block: 8, inline: 16, font: "14px", line: "20px" },
  [ButtonSize.Large]: { block: 12, inline: 24, font: "15px", line: "22px" },
};

const sizeStyles = (size: ButtonSize, variant: ButtonVariant) => {
  const { block, inline, font, line } = SIZES[size];
  const hairline = variant === ButtonVariant.Bordered ? 1 : 0;

  return `
    padding: ${block - hairline}px ${inline - hairline}px;
    font-size: ${font};
    line-height: ${line};
  `;
};

// Transient, so none of them lands on the element as an attribute.
interface CustomButtonProps {
  $variant: ButtonVariant;
  $color: ButtonColor;
  $size: ButtonSize;
  $fullWidth?: boolean;
}

export const CustomButton = styled.button<CustomButtonProps>`
  cursor: pointer;
  border-radius: 2px;
  font-weight: 500;
  ${({ $size, $variant }) => sizeStyles($size, $variant)}
  ${({ $fullWidth }) => ($fullWidth ? "width: 100%;" : "")}
  transition:
    box-shadow 0.2s,
    background-color 0.2s,
    color 0.2s,
    border 0.2s;

  ${({ $color, $variant, theme }) => {
    const paint = resolveButtonColor($color, $variant, theme);
    return $variant === ButtonVariant.Filled
      ? getBackgroundColor(paint, theme, resolveFilledLabel($color, theme))
      : variantStyles[$variant](paint, theme);
  }};

  ${focusOutline}
`;

// The dots take the label's colour, so the spinner reads wherever the label
// does: white or black on a fill, the text colour on the page.
export const Spinner = styled(PreLoader)`
  margin-right: 5px;
  width: 20px;
  height: 14px;
  transform-origin: 0 -2px;
  transform: scale(0.3);

  && div div::after {
    background: currentColor;
  }
`;

// An icon in front of the label, after it, or both. They sit in one inline
// row so the label's own text keeps flowing as text.
export const WithIcons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  vertical-align: top;
`;
